<script setup>
import {ref, computed, onMounted} from "vue";
import {definePage} from "unplugin-vue-router/runtime";
import createApiService from "@/services/api.js";
import {useRoute, useRouter} from "vue-router";
import checkIcon from '@/assets/check-circle.svg';

definePage({
  meta: {
    title: 'Taak uitvoeren',
    requiresAuth: true,
    layout: 'client-task',
  },
})

const loading = ref(true);
const router = useRouter();
const route = useRoute();
const api = createApiService(router);
const errors = ref([]);

import {useMessageStore} from "@/stores/message.js";
const messageStore = useMessageStore();

const task = ref(null)
const totalSteps = ref(0);
const stepNr = ref(1);
const progressColor = computed(() => stepNr.value === totalSteps ? 'success' : 'secondary');

const getTask = async () => {

  try {
    const response = await api.post('getclienttask', {id: route.params.id});

    if (response.status === 200) {
      task.value = response.data.data.task;
      totalSteps.value = Object.keys(task.value.steps).length;
    }
    else {
      messageStore.addMessage('Er is iets misgegaan. Probeer het later nog eens.', 'error')
      await router.push('/client');
    }
  } catch (e) {
    let message = e.response?.status === 400 ? e.response.data.message : 'Er is iets misgegaan. Probeer het later nog eens.';
    messageStore.addMessage(message, 'error')
    await router.push('/client');
  } finally {
    loading.value = false;
  }
}

const finishTask = async () => {
  loading.value = true;

  try {
    const response = await api.post('finishclienttask', {id: route.params.id});
    if (response.status === 200) {
      messageStore.addMessage('Taak succesvol afgerond')
      await router.push('/client');
    }
    else {
      errors.value = ['Er is iets misgegaan. Probeer het later nog eens.'];
    }
  } catch (e) {
    errors.value = e.response?.status === 400 ? [e.response.data.data] : ['Er is iets misgegaan. Probeer het later nog eens.'];
  } finally {
    loading.value = false;
  }
}

window.addEventListener('mouseup', (e) => {
  e.preventDefault();
  if (e.button === 3) { // Mouse4: go back a step or to home if on first step
    if (stepNr.value > 1) {
      stepNr.value--;
    }
    else {
      router.push('/');
    }
  }
  else if (e.button === 4) { // Mouse5: go forward a step if not at last step
    if (stepNr.value < totalSteps.value) {
      stepNr.value++;
    }
  }
});

onMounted(async () => {
  await getTask();
})
</script>

<template>
  <v-progress-linear
    class="w-100"
    height="16"
    :color="progressColor"
    min="1" :max="totalSteps"
    v-model="stepNr"
  ></v-progress-linear>

  <v-alert
    v-if="errors.length > 0"
    class="mx-auto"
    max-width="500"
    color="error"
    variant="elevated"
  >
    <template #text>
            <span
              v-for="(error, index) in errors"
              :key="index"
            >
              {{ error }}
            </span>
    </template>
  </v-alert>

  <v-card
    v-if="!loading"
    class="h-100 px-6 py-4 rounded-0 d-flex flex-column min-vh-100"
    :height="'100dvh'"
  >
    <v-card-title style="max-width: 500px;" class="w-100 mx-auto">
      <p class="p-small text-center pb-1">Stap {{ stepNr }} van {{ totalSteps }}</p>
      <h3 class="text-center">{{ task.title }}</h3>
    </v-card-title>

    <div class="flex-grow-1 overflow-auto pb-6 mx-auto w-100" style="max-width: 500px;">
      <v-stepper
        v-if="task"
        v-model="stepNr"
        :items="task.steps"
        class="h-100 d-flex flex-column"
        hide-actions
      >
        <v-sheet min-height="200" class="mb-8">
          <v-img class="py-4 rounded-lg mb-4" max-height="400" :src="task.steps[stepNr - 1].image"></v-img>
          <div id="step-info">
            <h3>{{ task.steps[stepNr - 1].nr }}. {{ task.steps[stepNr - 1].title }}</h3>
            <ul v-if="task.steps[stepNr - 1]?.description.length > 0">
              <li>
                <h4 class="font-weight-regular" v-html="task.steps[stepNr - 1].description.replace(/\n/g, '<br>')"/>
              </li>
            </ul>
          </div>
        </v-sheet>
      </v-stepper>
    </div>

    <div class="sticky-actions bg-white pt-2 mx-auto w-100" style="max-width: 500px;">
      <template v-if="task">
        <v-btn v-if="stepNr < totalSteps"
               height="54"
               variant="elevated"
               color="secondary"
               width="100%"
               @click="stepNr++"
        >
          <h4>Volgende stap</h4>
        </v-btn>
        <template v-else>
          <v-card
            class="d-flex flex-column align-center justify-center pa-8 w-100 rounded-lg"
            color="zieGray3"
          >
            <v-img class="mb-2" :src="checkIcon" width="29" height="24"></v-img>
            <h3 class="mb-6">Is het gelukt?</h3>
            <v-btn
              height="54"
              variant="elevated"
              color="secondary"
              width="100%"
              @click="finishTask()"
            >
              <h4>Taak afronden</h4>
            </v-btn>
          </v-card>
        </template>

        <v-btn
          v-if="stepNr > 1"
          class="font-weight-bold opacity-100 w-100 mt-4"
          style="letter-spacing: 0"
          color="secondary"
          variant="plain"
          @click="stepNr--"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512" height="14"
               style="font-weight: 900; line-height: 120%; font-size: 14px; margin-right: 6px">
            <path fill="rgb(76, 123, 242)"
                  d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l192 192c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L77.3 256 246.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-192 192z"
            />
          </svg>
          Vorige stap
        </v-btn>

        <v-btn
          v-else
          class="font-weight-bold opacity-100 w-100 mt-4"
          style="letter-spacing: 0"
          color="secondary"
          variant="plain"
          @click="router.push('/client')"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512" height="14"
               style="font-weight: 900; line-height: 120%; font-size: 14px; margin-right: 6px">
            <path fill="rgb(76, 123, 242)"
                  d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l192 192c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L77.3 256 246.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-192 192z"
            />
          </svg>
          Terug naar beginscherm
        </v-btn>
      </template>
    </div>
  </v-card>
</template>

<style>
.v-stepper {
  .v-stepper-header {
    display: none !important;
  }

  .v-stepper-window {
    margin: 0;
  }
}
</style>

<style scoped lang="scss">
ul {
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    position: relative;
    padding-left: 20px;

    &::before {
      content: "•";
      position: absolute;
      left: 7px;
    }
  }
}

.sticky-actions {
  position: sticky;
  bottom: 0;
  z-index: 10;
}
</style>
